import { useRef, useState } from "react";
import { FiPlay, FiVideo } from "react-icons/fi";
import { formatDuration } from "../config";
import { MediaLightbox } from "./MediaLightbox";

interface VideoPreviewProps {
  /** Video URL */
  url: string;
  /** Filename for display */
  filename: string;
  /** Duration in seconds */
  duration?: number | null | undefined;
  /** Preview variant */
  variant: "inline" | "full";
  /** Optional className */
  className?: string | undefined;
}

/**
 * VideoPreview - Video thumbnail and player
 *
 * Inline: Thumbnail with play overlay for table rows
 * Full: HTML5 video player for modals
 */
export function VideoPreview({
  url,
  filename,
  duration,
  variant,
  className = "",
}: VideoPreviewProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoError, setVideoError] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);

  if (variant === "inline") {
    return (
      <>
        <div
          className={`flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity ${className}`}
          onClick={() => setShowLightbox(true)}
        >
          <div className="relative h-10 w-10 shrink-0">
            {videoError ? (
              <div className="flex h-full w-full items-center justify-center rounded bg-gray-100">
                <FiVideo className="h-5 w-5 text-gray-400" />
              </div>
            ) : (
              <>
                {/* biome-ignore lint/a11y/useMediaCaption: Media preview displays user-uploaded content without captions */}
                <video
                  src={url}
                  className="h-full w-full rounded object-cover"
                  preload="metadata"
                  onError={() => setVideoError(true)}
                />
                <div className="absolute inset-0 flex items-center justify-center rounded bg-black/30">
                  <FiPlay className="h-4 w-4 text-white" />
                </div>
              </>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-gray-900">{filename}</p>
            {duration && <p className="text-xs text-gray-500">{formatDuration(duration)}</p>}
          </div>
        </div>

        {/* Lightbox */}
        {showLightbox && (
          <MediaLightbox
            media={{ type: "VIDEO", url, filename }}
            onClose={() => setShowLightbox(false)}
          />
        )}
      </>
    );
  }

  // Full variant for modals
  return (
    <div className={`space-y-4 ${className}`}>
      {videoError ? (
        <div className="flex h-64 items-center justify-center rounded-lg bg-gray-100">
          <div className="text-center">
            <FiVideo className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">Failed to load video</p>
          </div>
        </div>
      ) : showPlayer ? (
        // biome-ignore lint/a11y/useMediaCaption: Media preview displays user-uploaded content without captions
        <video
          ref={videoRef}
          src={url}
          controls
          autoPlay
          className="max-h-96 w-full rounded-lg"
          onError={() => setVideoError(true)}
        />
      ) : (
        <div className="relative cursor-pointer" onClick={() => setShowPlayer(true)}>
          {/* biome-ignore lint/a11y/useMediaCaption: Media preview displays user-uploaded content without captions */}
          <video
            src={url}
            className="max-h-96 w-full rounded-lg object-contain"
            preload="metadata"
            onError={() => setVideoError(true)}
          />
          <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/30 hover:bg-black/40">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90">
              <FiPlay className="h-8 w-8 text-gray-900 ml-1" />
            </div>
          </div>
          {duration && (
            <div className="absolute bottom-2 right-2 rounded bg-black/70 px-2 py-1 text-xs text-white">
              {formatDuration(duration)}
            </div>
          )}
        </div>
      )}

      <p className="text-sm text-gray-600">{filename}</p>
    </div>
  );
}

export default VideoPreview;
